/**
 * Requests of `ContentHubV2Provider.getContent` as pure functions: the provider runs them, the SSR middleware
 * (`server/middlewares/reactRenderer/urls.ts`) builds the same URLs to prefetch a content before the render.
 *
 * No aliased imports: the server code imports this module without the aliases.
 */
import type {TCmsQueryVariables} from '../cmsQueryTypes';

import type {QueryBuilder} from './QueryBuilder/QueryBuilder';

type TVariables = TCmsQueryVariables;

export type TEntriesConfig = {
	variables?: TVariables;
	fallbackVariables?: TVariables[];
};

/** Variables of one `getContent` call, before the requests are built. */
export interface IEntriesRequest {
	variables: TVariables;
	fallbackVariables: TVariables[];
	fetchAllLocales: boolean;
}

const localeRegex = /^(?!id$)[a-z]{2}([-_][A-Z]{2})?$/;

const clone = <T>(source: T): T => {
	try {
		return structuredClone(source);
	} catch {
		return source;
	}
};

/**
 * Adds default fallback variables for well-known content types when none are provided.
 */
export function applyDefaultFallbacks(id: string, variables: TVariables, fallbackVariables: TVariables[]): void {
	if (id === 'productDetails' && variables?.productId && fallbackVariables.length === 0) {
		fallbackVariables.push({
			productId: false,
			locale: variables.locale,
		});
	} else if (id === 'modal' && variables?.modalId && fallbackVariables.length === 0) {
		fallbackVariables.push({
			customId: variables.modalId,
			locale: variables.locale,
		});
	} else if (id === 'categoryPage' && variables?.category && variables?.type && fallbackVariables.length === 0) {
		fallbackVariables.push({
			type: variables.type,
			locale: variables.locale,
		});
	}
}

/**
 * Variables and fallbacks of a `getContent(id, config)` call in `currentLanguage` (the CMS language of the
 * provider): locale, slug moved to the current language, default fallbacks.
 */
export function prepareEntriesRequest(id: string, config: TEntriesConfig = {}, currentLanguage: string): IEntriesRequest {
	const fetchAllLocales = !!config.variables?.allLocales;
	const variables: TVariables = Object.assign({}, config.variables || {});

	if (!fetchAllLocales) {
		variables.locale = config.variables?.locale || currentLanguage;
	} else {
		delete variables.locale;
	}

	const fallbackVariables: TVariables[] = (config.fallbackVariables || []).map((v) => {
		const fallbackVariable = Object.assign({}, v || {});

		if (!fetchAllLocales) {
			fallbackVariable.locale = config.variables?.locale || currentLanguage;
		}

		return fallbackVariable;
	});

	const {slug} = variables;

	if (typeof slug === 'object' && slug !== null) {
		const slugLocales = Object.keys(slug);

		// The slug is searched in the current language, then in its own: a slug already in the current
		// language would only repeat the primary query (an extra request when the page does not exist).
		if (slugLocales.some((key) => key != currentLanguage)) {
			fallbackVariables.push(clone(variables));

			// A list is copied by index, as the spread of the original code did.
			const localizedSlug = Object.assign({}, slug) as TVariables;

			slugLocales.forEach((key: string) => {
				if (key != currentLanguage) {
					localizedSlug[currentLanguage] = localizedSlug[key];
					delete localizedSlug[key];
				}
			});

			variables.slug = localizedSlug;
		}
	}

	applyDefaultFallbacks(id, variables, fallbackVariables);

	return {variables, fallbackVariables, fetchAllLocales};
}

/**
 * Attempts to merge a single fallback into the primary variables in-place.
 * Mergeable when both share the same keys and locale-keyed object fields
 * have identical values across all locales.
 * Merge is atomic: primary is mutated only when all fields pass validation.
 *
 * @returns true if merged successfully, false if not mergeable (primary unchanged)
 */
function tryMergeSingleFallback(primary: TVariables, fallback: TVariables): boolean {
	const primaryKeys = Object.keys(primary).filter((k) => k !== 'locale');
	const fallbackKeys = Object.keys(fallback).filter((k) => k !== 'locale');

	if (primaryKeys.length !== fallbackKeys.length) {
		return false;
	}

	if (!primaryKeys.every((k) => fallbackKeys.includes(k))) {
		return false;
	}

	const pendingMerges: Array<{key: string; merged: TVariables}> = [];

	for (const key of primaryKeys) {
		const pVal = primary[key];
		const fVal = fallback[key];

		if (typeof pVal === 'object' && pVal !== null && typeof fVal === 'object' && fVal !== null) {
			const allLocales = Array.from(new Set(Object.keys(pVal).concat(Object.keys(fVal))));
			const isLocaleKeyed = allLocales.length > 0 && allLocales.every((k) => localeRegex.test(k));

			if (isLocaleKeyed) {
				const values = allLocales.map((k) => (pVal as TVariables)[k] ?? (fVal as TVariables)[k]);
				const reference = JSON.stringify(values[0]);

				if (!values.every((v) => JSON.stringify(v) === reference)) {
					return false;
				}

				pendingMerges.push({key, merged: {[allLocales.join(',')]: values[0]}});
			} else if (JSON.stringify(pVal) !== JSON.stringify(fVal)) {
				return false;
			}
		} else if (JSON.stringify(pVal) !== JSON.stringify(fVal)) {
			return false;
		}
	}

	for (const {key, merged} of pendingMerges) {
		primary[key] = merged;
	}

	return true;
}

/**
 * Attempts to merge fallback variable sets into the primary variables when they
 * only add locale-specific field filters with identical values.
 * Returns the merged primary and any remaining non-mergeable fallbacks.
 */
export function mergeFallbacks(
	variables: TVariables,
	fallbackVariables: TVariables[],
): {merged: TVariables; remaining: TVariables[]} {
	const merged = {...variables};
	const remaining: TVariables[] = [];

	for (const fallback of fallbackVariables) {
		if (!tryMergeSingleFallback(merged, fallback)) {
			remaining.push(fallback);
		}
	}

	return {merged, remaining};
}

/**
 * Applies preview-specific query params to a ContentHub query.
 */
const applyPreviewParams = (query: TVariables, isPreview: boolean, scheduledAt?: string): void => {
	if (!isPreview || !scheduledAt) {
		return;
	}

	query.status = 'published,changed';
	query.scheduled_at = scheduledAt;
};

/**
 * Query parameters of the requests of a `getContent` call: the primary query (fallbacks merged into it when
 * possible) and the fallback queries, run in order while the result is empty.
 */
export function buildEntriesQueries(
	queryBuilder: QueryBuilder,
	id: string,
	{variables, fallbackVariables, fetchAllLocales}: IEntriesRequest,
	{isPreview, scheduledAt}: {isPreview: boolean; scheduledAt?: string},
): {primary: TVariables; fallbacks: TVariables[]} {
	const {merged, remaining} = mergeFallbacks(variables, fallbackVariables);
	const primaryVars = {...merged};

	if (fetchAllLocales) {
		primaryVars.locale = '*';
	}

	const primary = queryBuilder.build({id, variables: primaryVars});

	primary.resolve = 'includes';
	applyPreviewParams(primary, isPreview, scheduledAt);

	const fallbacks = remaining
		.filter((currentVariables) => Object.keys(currentVariables).length > 0)
		.map((currentVariables) => {
			const fbVars = {...currentVariables};

			if (fetchAllLocales) {
				delete fbVars.locale;
			}

			const fallbackQuery = queryBuilder.build({id, variables: fbVars});

			fallbackQuery.resolve = 'includes';
			applyPreviewParams(fallbackQuery, isPreview, scheduledAt);

			return fallbackQuery;
		});

	return {primary, fallbacks};
}

/**
 * URL of the entries endpoint for `query` (parameters in the order of the query, empty values left out).
 */
export function buildEntriesUrl(baseUrl: string, query: TVariables): string {
	const url = new URL(baseUrl);

	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined && value !== null) {
			url.searchParams.append(key, String(value));
		}
	}

	return url.toString();
}
